import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Bell, CheckCircle, AlertTriangle, Clock, Download, Loader2, 
  Filter, RefreshCw, Eye, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const NotificacoesPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [notificacoes, setNotificacoes] = useState([]);
  const [filtro, setFiltro] = useState('todas');

  useEffect(() => {
    fetchNotificacoes();
  }, []);

  const fetchNotificacoes = async () => {
    setLoading(true);
    try {
      // Buscar alertas de banco de horas como notificações
      const alertasResponse = await api.get(`/banco-horas/alertas?empresa_id=${user?.empresa_id}`);
      
      // Transformar alertas em notificações
      const notifs = alertasResponse.data.map((alerta, index) => ({
        id: `alerta-${index}`,
        tipo: alerta.tipo === 'vencimento' ? 'aviso' : alerta.tipo === 'limite_negativo' ? 'erro' : 'info',
        titulo: alerta.tipo === 'vencimento' ? 'Vencimento de Banco de Horas' : 'Limite de Banco de Horas',
        mensagem: alerta.mensagem,
        empregado: alerta.empregado_nome,
        data: new Date().toISOString().split('T')[0],
        lida: false,
        prioridade: alerta.prioridade
      }));
      
      setNotificacoes(notifs);
    } catch (error) {
      console.error('Error fetching notificacoes:', error);
    } finally {
      setLoading(false);
    }
  };

  const marcarComoLida = (id) => {
    setNotificacoes(notificacoes.map(n => 
      n.id === id ? { ...n, lida: true } : n
    ));
    toast.success('Notificação marcada como lida');
  };

  const marcarTodasComoLidas = () => {
    setNotificacoes(notificacoes.map(n => ({ ...n, lida: true })));
    toast.success('Todas as notificações marcadas como lidas');
  };

  const getTipoBadge = (tipo) => {
    const tipos = {
      erro: { label: 'Erro', className: 'bg-red-500', icon: AlertTriangle },
      aviso: { label: 'Aviso', className: 'bg-amber-500', icon: AlertTriangle },
      info: { label: 'Info', className: 'bg-blue-500', icon: Bell },
      sucesso: { label: 'Sucesso', className: 'bg-emerald-500', icon: CheckCircle }
    };
    const config = tipos[tipo] || tipos.info;
    const Icon = config.icon;
    return (
      <Badge className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getPrioridadeBadge = (prioridade) => {
    const prioridades = {
      critica: { label: 'Crítica', className: 'bg-red-100 text-red-700 border-red-300' },
      alta: { label: 'Alta', className: 'bg-amber-100 text-amber-700 border-amber-300' },
      media: { label: 'Média', className: 'bg-blue-100 text-blue-700 border-blue-300' },
      baixa: { label: 'Baixa', className: 'bg-slate-100 text-slate-700 border-slate-300' }
    };
    const config = prioridades[prioridade] || prioridades.media;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  const notificacoesFiltradas = notificacoes.filter(n => {
    if (filtro === 'todas') return true;
    if (filtro === 'nao-lidas') return !n.lida;
    if (filtro === 'lidas') return n.lida;
    return n.tipo === filtro;
  });

  const naoLidas = notificacoes.filter(n => !n.lida).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notificacoes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 font-['Outfit'] mb-1">Notificações</h2>
          <p className="text-slate-500">Alertas e avisos do sistema</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchNotificacoes}
            data-testid="btn-atualizar"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          {naoLidas > 0 && (
            <Button
              onClick={marcarTodasComoLidas}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-marcar-todas"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Marcar Todas como Lidas
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 cursor-pointer hover:border-emerald-300 transition-colors"
              onClick={() => setFiltro('todas')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Total</p>
                <p className="text-2xl font-bold text-slate-800">{notificacoes.length}</p>
              </div>
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <Bell className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 cursor-pointer hover:border-emerald-300 transition-colors"
              onClick={() => setFiltro('nao-lidas')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Não Lidas</p>
                <p className="text-2xl font-bold text-amber-600">{naoLidas}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 cursor-pointer hover:border-emerald-300 transition-colors"
              onClick={() => setFiltro('aviso')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Avisos</p>
                <p className="text-2xl font-bold text-amber-600">
                  {notificacoes.filter(n => n.tipo === 'aviso').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 cursor-pointer hover:border-emerald-300 transition-colors"
              onClick={() => setFiltro('erro')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Erros</p>
                <p className="text-2xl font-bold text-red-600">
                  {notificacoes.filter(n => n.tipo === 'erro').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Notificações */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-['Outfit']">Lista de Notificações</CardTitle>
              <CardDescription>
                {filtro === 'todas' ? 'Todas as notificações' : 
                 filtro === 'nao-lidas' ? 'Notificações não lidas' :
                 filtro === 'lidas' ? 'Notificações lidas' :
                 `Notificações do tipo ${filtro}`}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <select
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm"
              >
                <option value="todas">Todas</option>
                <option value="nao-lidas">Não Lidas</option>
                <option value="lidas">Lidas</option>
                <option value="aviso">Avisos</option>
                <option value="erro">Erros</option>
                <option value="info">Informações</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {notificacoesFiltradas.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 mx-auto mb-3 text-slate-400" />
              <p className="text-slate-500">Nenhuma notificação encontrada</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notificacoesFiltradas.map((notif) => (
                <div
                  key={notif.id}
                  className={`p-4 rounded-lg border transition-colors ${
                    notif.lida 
                      ? 'bg-slate-50 border-slate-200' 
                      : 'bg-white border-slate-300 shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getTipoBadge(notif.tipo)}
                        {notif.prioridade && getPrioridadeBadge(notif.prioridade)}
                        {!notif.lida && (
                          <Badge className="bg-emerald-500">Nova</Badge>
                        )}
                      </div>
                      <h4 className={`font-medium ${notif.lida ? 'text-slate-600' : 'text-slate-800'}`}>
                        {notif.titulo}
                      </h4>
                      <p className={`text-sm mt-1 ${notif.lida ? 'text-slate-400' : 'text-slate-600'}`}>
                        {notif.mensagem}
                      </p>
                      {notif.empregado && (
                        <p className="text-xs text-slate-400 mt-2">
                          Empregado: {notif.empregado}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-xs text-slate-400">{notif.data}</span>
                      {!notif.lida && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => marcarComoLida(notif.id)}
                          className="text-emerald-600 hover:text-emerald-700"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Marcar como lida
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificacoesPage;
